/**
 * Settings section ids and the map from older ids onto them.
 *
 * Macro-free on purpose: the rail labels live in `groups.ts` (Lingui), and a
 * plain Vitest file cannot import that module. Deep links, a stale in-memory
 * tab, and the pre-regroup tab names all resolve through `resolveSettingsGroup`.
 */

export const SETTINGS_GROUPS = ["general", "look", "reports", "integrations", "advanced"] as const
export type TSettingsGroup = (typeof SETTINGS_GROUPS)[number]

/**
 * Previous rail values (recording, ai-models, spending, workspace) and the
 * older tab ids those replaced. `reports` and `integrations` kept their ids.
 * A card anchor is accepted too, so a hash that names a card still opens the
 * section that card moved to.
 */
const LEGACY_TO_GROUP: Record<string, TSettingsGroup> = {
  recording: "general",
  "ai-models": "advanced",
  spending: "advanced",
  workspace: "look",
  providers: "advanced",
  models: "advanced",
  prompt: "advanced",
  transcription: "general",
  "fallback-chains": "advanced",
  "image-generation": "advanced",
  "report-types": "reports",
  "summary-layout": "reports",
  budget: "advanced",
  appearance: "look",
  "card-transcription": "advanced",
  "card-language": "general",
  "card-speaker-recognition": "advanced",
  "card-finished-transcript": "advanced",
  "card-llm-providers": "advanced",
  "card-models": "advanced",
  "card-prompt": "advanced",
  "card-image-generation": "advanced",
  "card-report-types": "reports",
  "card-summary-layout": "reports",
  "card-budget": "advanced",
  "card-appearance": "look",
  "card-integrations": "integrations",
  "card-connect-claude": "integrations",
  "card-members": "general",
  "card-branding": "look",
}

function isSettingsGroup(value: string): value is TSettingsGroup {
  return (SETTINGS_GROUPS as readonly string[]).includes(value)
}

/** A current section id, an old one, or nothing — always a section that exists. */
export function resolveSettingsGroup(value: string | undefined): TSettingsGroup {
  if (!value) return "general"
  if (isSettingsGroup(value)) return value
  return LEGACY_TO_GROUP[value] ?? "general"
}
